import React, { useCallback, useEffect, useMemo, useState } from "react";
import KanbanBoard from "../components/KanbanBoard";
import {
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  PlusOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { Input, Button, Avatar, Breadcrumb, Select, Spin } from "antd";
import TaskDrawer from "../components/TaskDrawer";
import ListView from "../components/ListView";
import TaskFormDrawer from "../components/TaskFormDrawer";
import useProjects from "hooks/projects/useProjects";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import useIterations from "hooks/projects/useIterations";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useTasks from "hooks/projects/useTasks";
import { toast } from "react-toastify";
import { useTags } from "hooks";
import { addRecentItem } from "utils/recentItems";
import { useAuth } from "contexts/AuthContext";
import RecentItems from "../components/RecentItems";
import IterationDetailsDrawer from "../components/IterationDetailsDrawer";
import { useKanbanTasks } from "hooks/projects/useKanbanTasks";
import BulkImportModal from "../components/BulkImportModal";
import * as pmService from "services/pmService";
import { formatNameWithEmail, getTaskAssigneeInfo } from "utils/taskAssignee";
import useTaskTransfer from "hooks/projects/useTaskTransfer";
import TaskTransferDrawer from "../components/TaskTransferDrawer";

const categories = [
  { value: "TASK", label: "Task" },
  { value: "FEATURE_REQUEST", label: "Feature Request" },
  { value: "IMPROVEMENT", label: "Improvement" },
  { value: "BUG", label: "Bug" },
];
const UNKNOWN_ASSIGNEE_VALUE = "__unknown__";
const IterationDetails = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, iterationId } = useParams();
  const { projects, fetchTasksByProjectIteration, loading } = useProjects();
  const {
    tasks,
    setTasks,
    fetchTasksForColumn,
    hasMoreByStatus,
    loadingByStatus,
    resetTasks,
  } = useKanbanTasks(projectId, iterationId);
  const { user } = useAuth();
  const { createTask, cloneTask, updateTask, deleteTask } = useTasks();
  const { taskTransfer, bulkTaskTransfer } = useTaskTransfer();
  const { tags, loading: tagsLoading } = useTags();
  const { fetchIterationsByProject, loading: iterationsLoading } =
    useIterations();
  const [iterations, setIterations] = useState([]);

  // const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [activeTab, setActiveTab] = useState("kanban");
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isTransferDrawerOpen, setIsTransferDrawerOpen] = useState(false);
  const [mode, setMode] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [selectedAssigneeId, setSelectedAssigneeId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [isIterationDrawerOpen, setIsIterationDrawerOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [actualPoints, setActualPoints] = useState(null);
  const [selectedTaskIds, setSelectedTaskIds] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);

  const normalizedTasks = useMemo(
    () =>
      (tasks || []).map((task) => {
        const assignee = getTaskAssigneeInfo(task);
        return {
          ...task,
          assigneeName: assignee.label,
          assigneeEmail: assignee.assigneeEmail,
          assigneeKey: assignee.key,
        };
      }),
    [tasks],
  );

  const filteredTasks = useMemo(() => {
    let result = [...normalizedTasks];

    if (searchQuery.trim()) {
      const lower = searchQuery.toLowerCase();

      result = result.filter(
        (task) =>
          task.title?.toLowerCase().includes(lower) ||
          task.description?.toLowerCase().includes(lower) ||
          task.assigneeName?.toLowerCase().includes(lower) ||
          task.assigneeEmail?.toLowerCase().includes(lower),
      );
    }

    if (selectedTagIds.length > 0) {
      result = result.filter((task) =>
        task.tags?.some((tag) => selectedTagIds.includes(tag.id)),
      );
    }

    if (selectedCategories.length > 0) {
      result = result.filter((task) => selectedCategories.includes(task.type));
    }

    if (selectedAssigneeId) {
      if (selectedAssigneeId === UNKNOWN_ASSIGNEE_VALUE) {
        result = result.filter(
          (task) =>
            !task?.assigneeKey ||
            !task?.assigneeName ||
            task.assigneeName === "Unknown",
        );
      } else {
        result = result.filter(
          (task) => task?.assigneeKey === selectedAssigneeId,
        );
      }
    }

    return result;
  }, [
    normalizedTasks,
    searchQuery,
    selectedTagIds,
    selectedCategories,
    selectedAssigneeId,
  ]);

  const project = useMemo(() => {
    if (!projects || projects.length === 0) return null;
    return projects.find((p) => String(p.id) === String(projectId));
  }, [projects, projectId]);

  const iteration = useMemo(() => {
    if (!iterations.length) return null;
    return iterations.find((i) => String(i.id) === String(iterationId));
  }, [iterations, iterationId]);

  const loadTasks = useCallback(
    async (hardRefresh = false) => {
      if (!projectId || !iterationId) return;

      try {
        if (hardRefresh) {
          resetTasks();
        }

        await Promise.all([
          fetchTasksForColumn("OPEN"),
          fetchTasksForColumn("IN_PROGRESS"),
          fetchTasksForColumn("BLOCKED"),
          fetchTasksForColumn("COMPLETED"),
          fetchTasksForColumn("RESOLVED"),
          fetchTasksForColumn("BACKLOG"),
          fetchTasksForColumn("CANCELLED"),
        ]);

        const iterationData = await fetchIterationsByProject(projectId);
        setIterations(iterationData || []);

        pmService
          .getIterationPoints(projectId, iterationId)
          .then(setActualPoints)
          .catch(() => setActualPoints(null));
      } catch (err) {
        console.error("Failed to load tasks", err);
        setTasks([]);
      }
    },
    [
      projectId,
      iterationId,
      fetchTasksForColumn,
      fetchIterationsByProject,
      resetTasks,
      setTasks,
    ],
  );

  const assignees = useMemo(() => {
    const byKey = new Map();
    normalizedTasks.forEach((task) => {
      if (!task.assigneeKey || !task.assigneeName) return;
      if (!byKey.has(task.assigneeKey)) {
        const optionLabel = formatNameWithEmail(
          task.assigneeName,
          task.assigneeEmail,
        );
        byKey.set(task.assigneeKey, {
          value: task.assigneeKey,
          label: optionLabel,
        });
      }
    });
    return [
      { value: UNKNOWN_ASSIGNEE_VALUE, label: "Unknown" },
      ...byKey.values(),
    ];
  }, [normalizedTasks]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const handleBulkImported = async (response) => {
    if ((response?.success || 0) > 0) {
      toast.success(`Successfully imported ${response.success} task(s)`);
    }
    if ((response?.failed || 0) > 0) {
      toast.error(`${response.failed} row(s) failed during import`);
    }
    await loadTasks();
  };

  useEffect(() => {
    if (location.state?.openTaskId) {
      const taskId = location.state.openTaskId;

      // find the task
      const taskToOpen = normalizedTasks.find((t) => t.id === taskId);

      if (taskToOpen) {
        setSelectedTask(taskToOpen);
        setMode("view");

        // clear the state so it doesn't reopen drawer again
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.pathname, location.state, navigate, normalizedTasks]);

  useEffect(() => {
    const taskId = searchParams.get("taskId");
    if (taskId) {
      // find task in your tasks list
      const taskToOpen = normalizedTasks.find(
        (t) => String(t.id) === String(taskId),
      );
      if (taskToOpen) {
        setSelectedTask(taskToOpen);
        setMode("view");
        // Optionally scroll to it in the list or Kanban

        // clear the state so it doesn't reopen drawer again
        navigate(location.pathname, { replace: true });
      }
    }
  }, [location.pathname, navigate, normalizedTasks, searchParams]);

  const handleViewTask = (task) => {
    addRecentItem(user.id, {
      id: task.id,
      type: "task",
      title: task.title,
      projectId,
      iterationId,
      path: `/general/projects/${projectId}/iterations/${iterationId}`,
      openedAt: new Date(Date.now()).toISOString(),
    });
    setSelectedTask(task);
    setMode("view");
  };

  const handleEditTask = (task) => {
    setMode("edit");
    setSelectedTask(task);
    setIsDrawerOpen(true);
  };

  const handleTaskUpdated = (updatedTask) => {
    if (!updatedTask?.id) return;

    setTasks((prev) =>
      prev.map((t) => (t.id === updatedTask.id ? { ...t, ...updatedTask } : t)),
    );

    setSelectedTask((prev) =>
      prev && prev.id === updatedTask.id ? { ...prev, ...updatedTask } : prev,
    );
  };

  const runOptimisticUpdate = async ({
    optimisticUpdate,
    mutationFn,
    successMessage,
    errorMessage,
  }) => {
    const previousState = tasks;

    // apply optimistic UI update
    setTasks(optimisticUpdate(previousState));

    try {
      await mutationFn();
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (error) {
      // rollback
      setTasks(previousState);
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      toast.error(backendMessage || errorMessage || "Operation failed");
      console.error(error);
      return false;
    }
  };

  const handleSubmitTask = async (values) => {
    setDrawerLoading(true);

    const tagIds = values.tags || [];
    const fullTags = tags.filter((t) => tagIds.includes(t.id));

    try {
      if (mode === "edit") {
        const payload = {
          type: values.type || selectedTask?.type || "TASK",
          ...values,
          iterationId: iterationId,
          tagIds: tagIds,
        };

        await runOptimisticUpdate({
          optimisticUpdate: (prev) =>
            prev.map((it) =>
              it.id === selectedTask.id
                ? { ...it, ...values, tags: fullTags }
                : it,
            ),

          mutationFn: async () => {
            const updated = await updateTask(
              projectId,
              selectedTask.id,
              payload,
              user?.id,
            );
            setTasks((prev) =>
              prev.map((it) =>
                it.id === selectedTask.id ? { ...updated, tags: fullTags } : it,
              ),
            );
            setSelectedTask(updated || null);
          },

          successMessage: "Iteration updated successfully",
          errorMessage: "Failed to update iteration",
        });
      } else {
        const tempId = Date.now();

        // inject iterationId here
        const payload = {
          type: values.type || "TASK",
          ...values,
          iterationId: iterationId, // backend expects number
        };

        const optimisticTask = {
          id: tempId,
          ...payload,
          tags: fullTags,
        };

        await runOptimisticUpdate({
          optimisticUpdate: (prev) => [optimisticTask, ...prev],

          mutationFn: async () => {
            const created = await createTask(projectId, payload, user?.id);

            // replace temp item with real backend item
            setTasks((prev) =>
              prev.map((it) =>
                it.id === tempId ? { ...created, tags: fullTags } : it,
              ),
            );
            setSelectedTask(created);
            setMode("view");
          },

          successMessage: "Task created successfully",
          errorMessage: "Failed to create task",
        });
      }
    } finally {
      setDrawerLoading(false);
      setIsDrawerOpen(false);
      //setSelectedTask(null);
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;

    setDeleteLoading(true);

    const deletedItem = taskToDelete;

    try {
      await runOptimisticUpdate({
        optimisticUpdate: (prev) =>
          prev.filter((it) => it.id !== deletedItem.id),

        mutationFn: async () => {
          await deleteTask(projectId, deletedItem.id);
        },

        successMessage: "Task deleted successfully",
        errorMessage: "Failed to delete task",
      });
    } finally {
      setDeleteLoading(false);
      setTaskToDelete(null);
      setDeleteModalOpen(false);
    }
  };

  const handleCloneTask = async (task) => {
    if (!task?.id) return;

    setDrawerLoading(true);
    try {
      const cloned = await cloneTask(projectId, task);
      toast.success("Task cloned successfully");
      await loadTasks(true);
      if (cloned?.id) {
        setSelectedTask(cloned);
        setMode("view");
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message;
      toast.error(backendMessage || "Failed to clone task");
      console.error(error);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleTaskTransfer = (task) => {
    setMode("transfer");
    setSelectedTask(task);
    setIsTransferDrawerOpen(true);
  };

  const handleTaskTransferSubmit = async ({
    taskId,
    targetIterationId,
    reason,
  }) => {
    const previousTasks = tasks;

    // optimistic remove from current iteration board
    setTasks((prev) => prev.filter((t) => t.id !== taskId));

    try {
      await taskTransfer(projectId, taskId, {
        targetIterationId,
        reason,
      });
    } catch (err) {
      setTasks(previousTasks);

      toast.error(err?.response?.data?.message || "Failed to transfer task");
    }
  };

  const handleToggleSelect = (ids, select = true) => {
    setSelectedTaskIds((prev) => {
      let updated = [...prev];

      if (Array.isArray(ids)) {
        if (select) {
          // add many
          ids.forEach((id) => {
            if (!updated.includes(id)) {
              updated.push(id);
            }
          });
        } else {
          // remove many
          updated = updated.filter((id) => !ids.includes(id));
        }
      } else {
        // single toggle
        if (updated.includes(ids)) {
          updated = updated.filter((id) => id !== ids);
        } else {
          updated.push(ids);
        }
      }

      return updated;
    });
  };

  const handleBulkTransferSubmit = async ({ targetIterationId, reason }) => {
    if (selectedTaskIds.length === 0) return;

    const previousTasks = tasks;

    // optimistic remove
    setTasks((prev) => prev.filter((t) => !selectedTaskIds.includes(t.id)));

    try {
      await bulkTaskTransfer(projectId, {
        taskIds: selectedTaskIds,
        targetIterationId,
        reason,
      });

      setSelectedTaskIds([]);
      setIsMultiSelectMode(false);
    } catch (err) {
      setTasks(previousTasks);

      toast.error(err?.response?.data?.message || "Bulk transfer failed");
    }
  };

  if (loading || iterationsLoading || tagsLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spin />
      </div>
    );
  }

  return (
    <div className="project-page">
      {/* Header */}
      <div className="project-header">
        <div className="project-title">
          <div className="project-icon">C</div>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <h1 style={{ margin: 0, fontSize: 18 }}>{iteration?.name}</h1>

              <EyeOutlined
                style={{
                  fontSize: 16,
                  color: "#6b7280",
                  cursor: "pointer",
                  transition: "0.2s",
                }}
                onClick={() => setIsIterationDrawerOpen(true)}
                onMouseEnter={(e) => (e.target.style.color = "#111827")}
                onMouseLeave={(e) => (e.target.style.color = "#6b7280")}
              />
            </div>
            <Breadcrumb
              style={{
                marginTop: 4,
                fontSize: 13,
              }}
              items={[
                {
                  title: (
                    <span
                      style={{
                        cursor: "pointer",
                        color: "#6b7280",
                        fontFamily: "Inter",
                      }}
                      onClick={() => navigate("/general/projects")}
                    >
                      Projects
                    </span>
                  ),
                },
                {
                  title: (
                    <span
                      style={{
                        cursor: iteration ? "pointer" : "default",
                        color: iteration ? "#6b7280" : "#111827",
                        fontWeight: iteration ? 400 : 500,
                        fontFamily: "Inter",
                      }}
                      onClick={() => {
                        if (iteration)
                          navigate(`/general/projects/${project?.id}`);
                      }}
                    >
                      {project?.name}
                    </span>
                  ),
                },
                ...(iteration
                  ? [
                      {
                        title: (
                          <span
                            style={{
                              color: "#111827",
                              fontWeight: 500,
                              fontFamily: "Inter",
                            }}
                          >
                            {iteration?.name ||
                              iteration?.key ||
                              iteration?.type ||
                              "Iteration"}
                          </span>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <Button
            onClick={() => {
              setIsMultiSelectMode((prev) => !prev);
              setSelectedTaskIds([]);
            }}
          >
            {isMultiSelectMode ? "Cancel Selection" : "Bulk Task Transfer"}
          </Button>
          <RecentItems />
          <Avatar.Group max={3}>
            <Avatar>AL</Avatar>
            <Avatar>DT</Avatar>
            <Avatar>MK</Avatar>
          </Avatar.Group>
        </div>
      </div>

      <div className="project-toolbar">
        <div className="custom-tabs">
          <div
            className={`tab-item ${activeTab === "kanban" ? "active" : ""}`}
            onClick={() => setActiveTab("kanban")}
          >
            <AppstoreOutlined />
            Kanban
          </div>

          <div
            className={`tab-item ${activeTab === "list" ? "active" : ""}`}
            onClick={() => setActiveTab("list")}
          >
            <UnorderedListOutlined />
            List
          </div>
        </div>

        {/* Right - Controls */}
        <div className="toolbar-actions">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 160 }}
          />

          <Select
            mode="multiple"
            placeholder="Filter by category"
            style={{ width: 160, fontFamily: "Inter" }}
            value={selectedCategories}
            onChange={setSelectedCategories}
            options={categories}
            maxTagCount={1}
          />

          <Select
            placeholder="Filter by assignee"
            style={{ width: 150, fontFamily: "Inter" }}
            value={selectedAssigneeId}
            onChange={setSelectedAssigneeId}
            options={assignees}
            allowClear
          />

          <Button
            icon={<UploadOutlined />}
            onClick={() => setIsBulkImportOpen(true)}
          >
            Import Tasks
          </Button>

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="new-task-btn"
            onClick={() => {
              setMode("create");
              setIsDrawerOpen(true);
            }}
          >
            New WorkItem
          </Button>
        </div>
      </div>

      {selectedTagIds.length > 0 && (
        <div className="active-filter">
          Filtering by tag:
          {selectedTagIds.map((tag) => (
            <span key={tag} className="active-filter-tag">
              {tag}
              <span
                className="remove-filter"
                onClick={() =>
                  setSelectedTagIds((prev) => prev.filter((t) => t !== tag))
                }
              >
                ✕
              </span>
            </span>
          ))}
          <span className="clear-all" onClick={() => setSelectedTagIds([])}>
            Clear all
          </span>
        </div>
      )}

      {/* Kanban */}
      {activeTab === "kanban" && (
        <KanbanBoard
          tasks={filteredTasks}
          fetchTasksForColumn={fetchTasksForColumn}
          hasMoreByStatus={hasMoreByStatus}
          setTasks={setTasks}
          onTaskClick={handleViewTask}
          onEditTask={handleEditTask}
          onCloneTask={handleCloneTask}
          onTransferTask={handleTaskTransfer}
          onDelete={(task) => {
            setTaskToDelete(task);
            setDeleteModalOpen(true);
          }}
          onAssigneeClick={(assignee) => {
            setSelectedAssigneeId((prev) =>
              prev === assignee ? null : assignee,
            );
          }}
          // handle bulk task transfer
          setSelectedTask={setSelectedTask}
          setIsTransferDrawerOpen={setIsTransferDrawerOpen}
          isMultiSelectMode={isMultiSelectMode}
          selectedTaskIds={selectedTaskIds}
          onToggleSelect={handleToggleSelect}
        />
      )}

      {activeTab === "list" && (
        <ListView
          tasks={filteredTasks}
          onTaskClick={handleViewTask}
          onEditTask={handleEditTask}
          onCloneTask={handleCloneTask}
          onDelete={(task) => {
            setTaskToDelete(task);
            setDeleteModalOpen(true);
          }}
        />
      )}

      {/* Drawer */}
      <TaskDrawer
        task={selectedTask}
        visible={mode === "view" && !!selectedTask}
        onClose={() => setSelectedTask(null)}
        onEditTask={handleEditTask}
        onTaskUpdated={handleTaskUpdated}
      />

      <TaskFormDrawer
        open={isDrawerOpen}
        mode={mode}
        initialValues={mode === "edit" ? selectedTask : null}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedTask(null);
        }}
        onSubmit={handleSubmitTask}
        loading={drawerLoading}
      />

      <IterationDetailsDrawer
        open={isIterationDrawerOpen}
        onClose={() => setIsIterationDrawerOpen(false)}
        iteration={iteration}
        project={project}
        tasks={tasks}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        setShow={setDeleteModalOpen}
        loading={deleteLoading}
        itemName="Task"
        message={
          taskToDelete
            ? `Are you sure you want to delete "${taskToDelete.title}" task? This action cannot be undone.`
            : undefined
        }
        onConfirm={handleDeleteTask}
      />

      <BulkImportModal
        open={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        projectId={projectId}
        iterationId={iterationId}
        actorId={user?.id}
        onImported={handleBulkImported}
      />

      <TaskTransferDrawer
        open={isTransferDrawerOpen}
        task={selectedTask}
        iterations={iterations}
        currentIterationId={iterationId}
        isBulk={selectedTaskIds.length > 0}
        selectedCount={selectedTaskIds.length}
        loading={drawerLoading}
        onClose={() => {
          setIsTransferDrawerOpen(false);
          setSelectedTask(null);
        }}
        // onSubmit={handleTaskTransferSubmit}
        onSubmit={
          selectedTaskIds.length > 0
            ? handleBulkTransferSubmit
            : handleTaskTransferSubmit
        }
      />
    </div>
  );
};

export default IterationDetails;
